/**
 * Bulletproof error normalizer for Safari and all browsers.
 * Safari can throw null/undefined/string as "error" - this handles ALL cases.
 */
export type AnyErr = unknown;

export interface SafeErrorInfo {
  name: string;
  message: string;
  stack?: string;
  raw: unknown;
}

export function safeError(err: AnyErr): SafeErrorInfo {
  // Safari can give null/undefined as "error"
  if (err == null) {
    return { 
      name: "Error", 
      message: "Unknown error (null/undefined)", 
      stack: undefined,
      raw: err 
    };
  }
  
  // Handle string errors
  if (typeof err === "string") {
    return { 
      name: "Error", 
      message: err || "Empty string error", 
      stack: undefined,
      raw: err 
    };
  }
  
  // Handle Error objects and error-like objects
  if (typeof err === "object") {
    const any = err as Record<string, unknown>;
    
    // Safely extract name - NEVER access directly
    let name = "Error";
    try {
      if (typeof any.name === "string" && any.name.trim()) {
        name = any.name;
      }
    } catch {
      // Property access failed, use default
    }
    
    // Safely extract message - NEVER access directly
    let message = "Unknown error";
    try {
      if (typeof any.message === "string" && any.message.trim()) {
        message = any.message;
      } else if (typeof any.error === "string" && any.error.trim()) {
        message = any.error;
      } else if (typeof any.msg === "string" && any.msg.trim()) {
        message = any.msg;
      } else if (typeof any.toString === "function") {
        const str = String(any.toString());
        if (str && str !== "[object Object]") {
          message = str;
        }
      }
    } catch {
      // Property access failed, use default
    }
    
    // Safely extract stack
    let stack: string | undefined;
    try {
      if (typeof any.stack === "string") {
        stack = any.stack;
      }
    } catch {
      // Property access failed
    }
    
    return { name, message, stack, raw: err };
  }
  
  // Handle numbers, booleans, symbols, functions
  return { 
    name: "Error", 
    message: String(err), 
    stack: undefined,
    raw: err 
  };
}
